import type { ValidationIssue, ValidationState } from '@/api/models';

/**
 * Normalised, money-parsed view of one editor line, in integer **tetri**.
 * `null` on a side means that field is empty (nothing typed); `0` means a zero
 * was typed. `accountId` is `null`/`''` when unset.
 */
export type ValidationLineInput = {
    accountId: string | null;
    debitTetri: number | null;
    creditTetri: number | null;
};

const hasAmount = (tetri: number | null): boolean =>
    tetri !== null && tetri > 0;

const isEmptyLine = (line: ValidationLineInput): boolean =>
    !line.accountId && line.debitTetri === null && line.creditTetri === null;

/**
 * The single source of truth for journal-entry validity — the same rule set the
 * POST enforces, computed in integer tetri. Runs in two places against this one
 * implementation: the form calls it on every keystroke for the live Post gate,
 * and the mock posting engine calls it as the authority at save/post. Because
 * both share this function they cannot drift.
 *
 * Rules (an entry is postable only when all hold):
 *  - at least two lines with content (fully-empty rows are ignored / dropped),
 *  - every non-empty line has an account and exactly one side with an amount,
 *  - total debits equal total credits (balanced),
 *  - the date's period is open.
 *
 * `issues` is ordered most-actionable-first: too-few-lines, then per-line
 * completeness in row order, then balance, then period — so the UI can show
 * `issues[0]` as the Post button's reason while the period warning renders
 * independently near the date.
 */
export const computeValidationState = (
    lines: ValidationLineInput[],
    opts: { periodClosed: boolean },
): ValidationState => {
    const issues: ValidationIssue[] = [];

    const contentLines = lines.filter((line) => !isEmptyLine(line));
    if (contentLines.length < 2) {
        issues.push({ code: 'TOO_FEW_LINES' });
    }

    lines.forEach((line, lineIndex) => {
        if (isEmptyLine(line)) {
            return; // an unused row is dropped on save, never an error itself
        }
        if (!line.accountId) {
            issues.push({ code: 'LINE_MISSING_ACCOUNT', lineIndex });
            return;
        }
        const debit = hasAmount(line.debitTetri);
        const credit = hasAmount(line.creditTetri);
        if (debit && credit) {
            issues.push({ code: 'LINE_TWO_SIDED', lineIndex });
            return;
        }
        if (!debit && !credit) {
            issues.push({ code: 'LINE_MISSING_AMOUNT', lineIndex });
        }
    });

    const totalDebitTetri = lines.reduce(
        (sum, line) => sum + (line.debitTetri ?? 0),
        0,
    );
    const totalCreditTetri = lines.reduce(
        (sum, line) => sum + (line.creditTetri ?? 0),
        0,
    );
    const differenceTetri = totalDebitTetri - totalCreditTetri;
    if (differenceTetri !== 0) {
        issues.push({ code: 'UNBALANCED' });
    }

    if (opts.periodClosed) {
        issues.push({ code: 'PERIOD_CLOSED' });
    }

    return {
        isPostable: issues.length === 0,
        issues,
        totalDebitTetri,
        totalCreditTetri,
        differenceTetri,
    };
};
